/**
 * Parse out the click conditions.
 *
 * @param {Boolean} clickOption - determines to check for a positive or negative condition (e.g. "clicked" or "not clicked")
 * @param {Object} condition - contains the conditions to check for
 * @param {Object} config - object containing the configs
 *
 * @return {Boolean}
 */
const parseClickedConditions = (
  clickOption,
  condition,
  { isClickTrue, clickCallback }
) => {
  if (clickOption && isClickTrue) {
    return true;
  } else if (!clickOption && isClickTrue) {
    return false;
  }

  const { selector } = condition;

  const element = document.querySelector(selector);

  if (!element) return false;

  element.addEventListener("click", clickCallback);

  return !clickOption;
};

/**
 * Parse out the exists conditions.
 *
 * @param {Boolean} existsOption - determines to check for a positive or negative condition (e.g. "exists" or "not exists")
 * @param {Object} condition - contains the conditions to check for
 *
 * @return {Boolean}
 */
const parseExistsConditions = (existsOption, condition) => {
  const { selector } = condition;

  const element = document.querySelector(selector);
  return existsOption ? !!element : !element;
};

/**
 * Parse out the hovered conditions.
 *
 * @param {Boolean} hoveredOption - determines to check for a positive or negative condition (e.g. "hovered" or "not hovered")
 * @param {Object} condition - contains the conditions to check for
 * @param {Object} config - object containing the configs
 *
 * @return {Boolean}
 */
const parseHoveredConditions = (
  hoveredOption,
  condition,
  { isHoveredTrue, hoverCallback }
) => {
  if (hoveredOption && isHoveredTrue) {
    return true;
  } else if (!hoveredOption && isHoveredTrue) {
    return false;
  }

  const { selector } = condition;

  const element = document.querySelector(selector);

  if (!element) return false;

  element.addEventListener("mouseover", hoverCallback);

  return !hoveredOption;
};

/**
 * Parse out the text conditions.
 *
 * @param {Boolean} textOption - determines to check for a positive or negative condition (e.g. "text contains" or "text does not contain")
 * @param {Object} condition - contains the conditions to check for
 *
 * @return {Boolean}
 */
const parseTextConditions = (textOption, condition) => {
  const { condition: textCondition, selector } = condition;

  const element = document.querySelector(selector);

  if (!element) return false;

  return textOption
    ? element.innerHTML.includes(textCondition)
    : !element.innerHTML.includes(textCondition);
};

/**
 * Parse out the visible conditions.
 *
 * @param {Boolean} visibleOption - determines to check for a positive or negative condition (e.g. "visible" or "not visible")
 * @param {Object} condition - contains the conditions to check for
 *
 * @return {Boolean}
 */
const parseVisibleConditions = (visibleOption, condition) => {
  const { selector } = condition;

  const element = document.querySelector(selector);

  if (!element) return false;

  if (visibleOption) {
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  } else {
    //return the inverse from the above code since we're looking for "not visible"
    return !(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  }
};

/**
 * Parse the mouse viewport conditions
 *
 * @param {Boolean} mouseViewportOption - determines to check for negative condition (e.g. "mouse not in viewport")
 * @param {Object} config - config that has callbacks
 *
 * @return {Boolean}
 */
const parseMouseViewportConditions = (
  mouseViewportOption,
  { isMouseLeftViewportTrue }
) => {
  return !!(!mouseViewportOption && isMouseLeftViewportTrue);
};

/**
 * Parse out the visible conditions.
 *
 * @param {Boolean} idleTimeoutOption - determines to check for a positive (e.g. idle timeout set)
 * @param {Object} condition - contains the conditions to check for (timeout in seconds)
 *
 * @return {Boolean}
 */
const parseIdleTimeoutConditions = (idleTimeoutOption, condition) => {
  return !!(idleTimeoutOption && condition);
};

/**
 * Runs the screen display engine, passes off work to smaller functions
 *
 * @param {Object} condition - object containing conditions
 * @param {Object} config - object containing the configs
 *
 * @return {Boolean}
 *
 */
export default (condition, config) => {
  const { options } = condition;

  switch (options) {
    case "clicked":
      return parseClickedConditions(true, condition, config);
    case "not clicked":
      return parseClickedConditions(false, condition, config);
    case "exists":
      return parseExistsConditions(true, condition);
    case "not exists":
      return parseExistsConditions(false, condition);
    case "hovered":
      return parseHoveredConditions(true, condition, config);
    case "not hovered":
      return parseHoveredConditions(false, condition, config);
    case "text contains":
      return parseTextConditions(true, condition);
    case "text does not contain":
      return parseTextConditions(false, condition);
    case "visible":
      return parseVisibleConditions(true, condition);
    case "not visible":
      return parseVisibleConditions(false, condition);
    case "mouse not in viewport":
      return parseMouseViewportConditions(false, config);
    case "idle time":
      return parseIdleTimeoutConditions(true, condition);
  }
};
